import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TICK } from "@/lib/chart";
import { Home, KeyRound } from "lucide-react";
import { http } from "@/lib/data";

const inr = (v) => `₹${Math.round(v).toLocaleString("en-IN")}`;
const cr = (v) => (Math.abs(v) >= 1e7 ? `₹${(v / 1e7).toFixed(2)} Cr` : `₹${(v / 1e5).toFixed(1)} L`);
const Num = ({ label, v, set, step = 1, testid }) => (
  <label className="text-xs font-semibold text-slate-500">{label}<input data-testid={testid} type="number" step={step} value={v} onChange={(e) => set(Number(e.target.value))} className="block mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 font-mono text-slate-900" /></label>
);

const simulate = ({ price, rentYield, growth, years, down, rate, tenure, rentHike, invest }) => {
  const loan = price * (1 - Math.min(100, Math.max(0, down)) / 100);
  const i = Math.max(0, rate) / 1200, n = Math.max(1, tenure) * 12;
  const emi = loan <= 0 ? 0 : i === 0 ? loan / n : (loan * i * (1 + i) ** n) / ((1 + i) ** n - 1);
  let bal = loan, rent = (price * rentYield) / 100 / 12, renterPot = price * (down / 100) + price * 0.07, buyerPot = 0;
  const mi = (1 + invest / 100) ** (1 / 12) - 1;
  const rows = [];
  for (let m = 1; m <= years * 12; m++) {
    const pay = m <= n ? emi : 0;
    if (m <= n) bal = Math.max(0, bal * (1 + i) - emi);
    const diff = pay - rent;
    renterPot = renterPot * (1 + mi) + Math.max(0, diff);
    buyerPot = buyerPot * (1 + mi) + Math.max(0, -diff);
    if (m % 12 === 0) {
      const y = m / 12;
      const value = price * (1 + growth / 100) ** y;
      rows.push({ year: y, buy: Math.round(value - bal + buyerPot), rent: Math.round(renterPot) });
      rent *= 1 + rentHike / 100;
    }
  }
  return { emi, rows, initialRent: (price * rentYield) / 100 / 12 };
};

export const RentVsBuy = ({ cityId = "chennai" }) => {
  const { data } = useQuery({ queryKey: ["re-cities"], queryFn: () => http.get("/re/cities").then((r) => r.data) });
  const cities = data?.items || [];
  const [cid, setCid] = useState(cityId);
  const city = cities.find((c) => c.id === cid) || cities[0];
  const [loc, setLoc] = useState("");
  const l = city?.localities.find((x) => x.name === loc) || city?.localities[0];
  const [p, setP] = useState({ size: 1100, years: 10, down: 20, rate: 8.5, tenure: 20, rentHike: 5, invest: 10 });
  const set = (k) => (v) => setP({ ...p, [k]: v });
  const growth = l ? Math.min(l.yoy, 8) : 6;
  const res = useMemo(() => (l ? simulate({ ...p, price: l.price_sqft * p.size, rentYield: l.rental_yield, growth }) : null), [l, p, growth]);
  if (!l) return null;
  const last = res.rows[res.rows.length - 1];
  const buyWins = last.buy > last.rent;

  return (
    <div data-testid="rent-vs-buy" className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm">
      <p className="font-display font-bold text-xl">Rent vs Buy</p>
      <p className="text-sm text-slate-500">Uses the locality's price, rental yield and growth. Whoever pays less each month invests the difference.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
        <label className="text-xs font-semibold text-slate-500">City<select data-testid="rvb-city" value={city.id} onChange={(e) => { setCid(e.target.value); setLoc(""); }} className="block mt-1 w-full h-10 rounded-lg border border-slate-300 px-2 text-slate-900">{cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
        <label className="text-xs font-semibold text-slate-500">Locality<select data-testid="rvb-locality" value={l.name} onChange={(e) => setLoc(e.target.value)} className="block mt-1 w-full h-10 rounded-lg border border-slate-300 px-2 text-slate-900">{city.localities.map((x) => <option key={x.name}>{x.name}</option>)}</select></label>
        <Num label="Home size (sq.ft)" v={p.size} set={set("size")} step={50} testid="rvb-size" />
        <Num label="Compare over (years)" v={p.years} set={(v) => set("years")(Math.min(30, Math.max(1, v)))} testid="rvb-years" />
        <Num label="Down payment (%)" v={p.down} set={set("down")} testid="rvb-down" />
        <Num label="Home loan rate (%)" v={p.rate} set={set("rate")} step={0.1} testid="rvb-rate" />
        <Num label="Loan tenure (years)" v={p.tenure} set={set("tenure")} testid="rvb-tenure" />
        <Num label="Renter's investment return (%)" v={p.invest} set={set("invest")} step={0.5} testid="rvb-invest" />
      </div>
      <div className="grid sm:grid-cols-4 gap-3 mt-5">
        {[["Property price", cr(l.price_sqft * p.size)], ["Monthly EMI", inr(res.emi)], ["Starting rent", `${inr(res.initialRent)}/mo`], ["Price growth used", `${growth}% / yr`]].map(([k, v]) => <div key={k} className="rounded-xl bg-[#F8FAFC] border border-slate-100 p-3"><p className="text-xs text-slate-500">{k}</p><p className="font-mono font-bold mt-1">{v}</p></div>)}
      </div>
      <div data-testid="rvb-verdict" className={`mt-5 rounded-2xl p-5 flex items-center gap-4 ${buyWins ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"}`}>
        {buyWins ? <Home className="w-8 h-8 text-[#059669]" /> : <KeyRound className="w-8 h-8 text-amber-600" />}
        <div><p className="font-display font-extrabold text-lg">{buyWins ? "Buying" : "Renting & investing"} leaves you richer after {p.years} years</p>
          <p className="text-sm text-slate-600">Net worth — Buy: <b className="font-mono">{cr(last.buy)}</b> · Rent: <b className="font-mono">{cr(last.rent)}</b> (difference {cr(Math.abs(last.buy - last.rent))})</p></div>
      </div>
      <div className="h-64 mt-5">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={res.rows}>
            <CartesianGrid stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="year" tick={TICK} tickFormatter={(y) => `Y${y}`} />
            <YAxis tick={TICK} width={70} tickFormatter={cr} />
            <Tooltip formatter={(v, k) => [cr(v), k === "buy" ? "Buy (home equity)" : "Rent (investments)"]} labelFormatter={(y) => `Year ${y}`} />
            <Legend formatter={(k) => (k === "buy" ? "Buy — home equity" : "Rent — invested savings")} />
            <Line dataKey="buy" stroke="#10B981" strokeWidth={2.5} dot={false} />
            <Line dataKey="rent" stroke="#F59E0B" strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-slate-500 mt-2">Assumes 7% upfront costs (stamp duty & registration) that the renter invests instead, rent rising {p.rentHike}% a year, and price growth capped at 8%. Ignores tax benefits and maintenance. Illustration only.</p>
    </div>
  );
};
